import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ConflictErrorCode,
	type FileConfigFieldError,
	type ImportDefinitionContract,
	type ImportDefinitionDTO,
	ImportDefinitionStatus,
	type ImportDefinitionVersionDTO,
	type TenantConfigPlane,
	ValidationErrorCode,
} from '@today/types'
import { isAxiosError } from 'axios'
import { importDefinitionsService } from '@/services/import-definitions.service'

/** Why a save or activation was refused, in the terms the designer shows it. */
export type ImportDefinitionSaveError =
	| { kind: 'invalid'; fields: FileConfigFieldError[] }
	| { kind: 'conflict' }
	| { kind: 'draft-exists' }
	| { kind: 'failed' }

interface ApiErrorBody {
	error?: { code?: string; details?: { fields?: FileConfigFieldError[] } }
}

/** Maps a refusal of the F5 routes to its kind (TO-414 Contract errors). */
export const saveErrorOf = (error: unknown): ImportDefinitionSaveError => {
	const body = isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error : undefined
	if (body?.code === ValidationErrorCode.IMPORT_DEFINITION_INVALID)
		return { kind: 'invalid', fields: body.details?.fields ?? [] }
	if (body?.code === ConflictErrorCode.IMPORT_DEFINITION_REVISION_CONFLICT) return { kind: 'conflict' }
	if (body?.code === ConflictErrorCode.IMPORT_DEFINITION_DRAFT_EXISTS) return { kind: 'draft-exists' }
	return { kind: 'failed' }
}

/** What Save is asked to store; `base` is the version the designer was opened from. */
export interface SaveRequest {
	key: string
	contract: ImportDefinitionContract
	base: { version: number; status: ImportDefinitionStatus; revision: number } | null
}

/**
 * A tenant's import definitions on the console plane's route tree, and the
 * designer's save + activate (TO-400 BR-12): a DRAFT being edited is replaced
 * under its revision; a key that exists gets the next version; a new key is
 * created at version 1.
 */
export function useImportDefinitions(tenantId: string, plane: TenantConfigPlane) {
	const client = useQueryClient()
	const queryKey = ['import-definitions', plane, tenantId] as const
	const query = useQuery({ queryKey, queryFn: () => importDefinitionsService.list(tenantId, plane) })
	const definitions: ImportDefinitionDTO[] | undefined = query.data?.items
	const refresh = () => client.invalidateQueries({ queryKey })

	const save = async ({ key, contract, base }: SaveRequest): Promise<ImportDefinitionVersionDTO> => {
		const saved =
			base?.status === ImportDefinitionStatus.DRAFT
				? await importDefinitionsService.updateDraft(tenantId, plane, key, base.version, base.revision, contract)
				: definitions?.some((d) => d.key === key)
					? await importDefinitionsService.addVersion(tenantId, plane, key, contract)
					: await importDefinitionsService.create(tenantId, plane, key, contract)
		await refresh()
		return saved
	}

	const activate = async (key: string, version: number): Promise<ImportDefinitionVersionDTO> => {
		const activated = await importDefinitionsService.activate(tenantId, plane, key, version)
		await refresh()
		return activated
	}

	const loadVersion = (key: string, version: number) =>
		importDefinitionsService.getVersion(tenantId, plane, key, version)

	return { definitions, loadFailed: query.isError, reload: query.refetch, save, activate, loadVersion }
}
